import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  FormLabel,
  FormGroup,
  FormHelperText,
  RadioGroup,
  Radio,
  Checkbox,
  Button,
  IconButton,
  Grid,
  Alert,
  AlertTitle,
  Snackbar,
  CircularProgress,
  Chip,
  Tooltip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Backup as BackupIcon,
  CloudDownload as DownloadIcon,
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Inventory2 as ArchiveIcon,
  Refresh as RefreshIcon,
  Restore as RestoreIcon,
  Save as SaveIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { usePermission } from '../../hooks/usePermission';
import { formatRelativeTime } from '../../utils/dateUtils';

const ARCHIVE_FORMAT = 'excubia-tenant-export';
const DEFAULT_SETTINGS = { weeklyExport: false, keepCount: 8 };

/** Friendly names for the collections a restore can write. */
const COLLECTION_LABELS = {
  detections: 'Detections',
  cases: 'Cases',
  indicators: 'Indicators',
  entities: 'Entities',
  agentConfigurations: 'Agent configurations',
  enrichmentProviders: 'Enrichment providers',
  automationRules: 'Automation rules',
  externalIntegrations: 'External integrations',
  notifications: 'Notifications',
  counters: 'Counters',
};

const labelFor = (name) => COLLECTION_LABELS[name]
  || String(name).replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

const serverMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

/** Error bodies come back as a Blob when the request asked for one, so unwrap them. */
const blobMessage = async (err, fallback) => {
  const data = err?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed.message || fallback;
    } catch (e) {
      return fallback;
    }
  }
  return serverMessage(err, fallback);
};

const toInt = (value, fallback) => {
  const n = parseInt(value, 10);
  return Number.isNaN(n) ? fallback : n;
};

const formatSize = (bytes) => {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = n / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) { value /= 1024; i += 1; }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[i]}`;
};

const formatAbsolute = (value) => {
  if (!value) return 'Unknown';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

const today = () => new Date().toISOString().slice(0, 10);

const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

const saveJson = (data, filename) => saveBlob(
  new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }),
  filename
);

/** Small name/count table used for export results and restore previews. */
const CountsTable = ({ counts }) => {
  const entries = Object.entries(counts || {});
  if (!entries.length) return <Typography variant="body2" color="text.secondary">No records.</Typography>;
  return (
    <TableContainer component={Paper} variant="outlined" sx={{ maxWidth: 420 }}>
      <Table size="small">
        <TableBody>
          {entries.map(([name, count]) => (
            <TableRow key={name}>
              <TableCell>{labelFor(name)}</TableCell>
              <TableCell align="right">{Number(count || 0).toLocaleString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const BackupSettings = () => {
  const { user, isCompanyAdmin } = usePermission();
  const organizationName = (user?.company?.name || '').trim();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [storageError, setStorageError] = useState(null);

  const [exports, setExports] = useState([]);
  const [restorable, setRestorable] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [savingSettings, setSavingSettings] = useState(false);

  const [downloading, setDownloading] = useState(false);
  const [storing, setStoring] = useState(false);
  const [lastExport, setLastExport] = useState(null);
  const [rowBusy, setRowBusy] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Restore dialog
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [source, setSource] = useState('stored');
  const [storedName, setStoredName] = useState(null);
  const [uploadName, setUploadName] = useState(null);
  const [archive, setArchive] = useState(null);
  const [archiveError, setArchiveError] = useState(null);
  const [mode, setMode] = useState('merge');
  const [selected, setSelected] = useState([]);
  const [confirmText, setConfirmText] = useState('');
  const [previewing, setPreviewing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [preview, setPreview] = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);
  const [restoreError, setRestoreError] = useState(null);

  const writeDisabled = !isCompanyAdmin;
  const writeTip = writeDisabled ? 'Only company administrators can create, delete or restore backups' : '';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.backups.get();
      setExports(res.data.exports || []);
      setRestorable(res.data.restorable || []);
      setSettings({ ...DEFAULT_SETTINGS, ...(res.data.settings || {}) });
      setStorageError(res.data.error || null);
    } catch (e) {
      setError(serverMessage(e, 'Failed to load backups'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    setError(null);
    try {
      const keepCount = Math.min(Math.max(toInt(settings.keepCount, 8), 1), 52);
      const res = await apiService.backups.updateSettings({ weeklyExport: !!settings.weeklyExport, keepCount });
      setSettings({ ...DEFAULT_SETTINGS, ...(res.data.settings || {}) });
      setNotice('Automatic export settings saved');
    } catch (e) {
      setError(serverMessage(e, 'Failed to save backup settings'));
    } finally {
      setSavingSettings(false);
    }
  };

  const handleDirectDownload = async () => {
    setDownloading(true);
    setError(null);
    try {
      const res = await apiService.backups.downloadExport();
      saveBlob(res.data, `excubia-export-${today()}.json`);
      setNotice('Export downloaded');
    } catch (e) {
      setError(await blobMessage(e, 'Export failed'));
    } finally {
      setDownloading(false);
    }
  };

  const handleStoreCopy = async () => {
    setStoring(true);
    setError(null);
    try {
      const res = await apiService.backups.createExport();
      setLastExport(res.data.export || null);
      setNotice('Export stored in Excubia');
      await load();
    } catch (e) {
      setError(serverMessage(e, 'Export failed'));
    } finally {
      setStoring(false);
    }
  };

  const handleDownloadStored = async (name) => {
    setRowBusy(name);
    setError(null);
    try {
      const res = await apiService.backups.exportDownload(name);
      if (res.data && res.data.url) {
        window.location.assign(res.data.url);
      } else {
        saveJson(res.data, `excubia-${name}`);
      }
    } catch (e) {
      setError(serverMessage(e, 'Download failed'));
    } finally {
      setRowBusy(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setError(null);
    try {
      await apiService.backups.deleteExport(deleteTarget.name);
      setNotice(`Export ${deleteTarget.name} deleted`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setError(serverMessage(e, 'Failed to delete the export'));
    } finally {
      setDeleting(false);
    }
  };

  const resetRestore = () => {
    setStoredName(null);
    setUploadName(null);
    setArchive(null);
    setArchiveError(null);
    setMode('merge');
    setSelected(restorable);
    setConfirmText('');
    setPreview(null);
    setRestoreResult(null);
    setRestoreError(null);
  };

  const openStoredRestore = (row) => {
    resetRestore();
    setSource('stored');
    setStoredName(row.name);
    setRestoreOpen(true);
  };

  const openUploadRestore = () => {
    resetRestore();
    setSource('upload');
    setRestoreOpen(true);
  };

  const closeRestore = () => {
    if (previewing || restoring) return;
    setRestoreOpen(false);
  };

  const handleFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    setArchive(null);
    setArchiveError(null);
    setPreview(null);
    setRestoreResult(null);
    setUploadName(file.name);
    try {
      const parsed = JSON.parse(await file.text());
      if (!parsed || parsed.format !== ARCHIVE_FORMAT) {
        setArchiveError(`${file.name} is not an Excubia tenant export (its "format" field must be "${ARCHIVE_FORMAT}").`);
        return;
      }
      setArchive(parsed);
      setSelected(restorable.filter((c) => Object.prototype.hasOwnProperty.call(parsed.counts || {}, c)));
    } catch (e) {
      setArchiveError(`${file.name} could not be parsed as JSON.`);
    }
  };

  const toggleCollection = (name) => {
    setSelected((prev) => (prev.includes(name) ? prev.filter((c) => c !== name) : [...prev, name]));
    setPreview(null);
  };

  const restorePayload = useCallback((dryRun) => {
    const payload = {
      source,
      mode,
      collections: selected,
      dryRun,
      ...(dryRun ? {} : { confirm: confirmText.trim() }),
    };
    if (source === 'stored') payload.name = storedName;
    else payload.archive = archive;
    return payload;
  }, [source, mode, selected, confirmText, storedName, archive]);

  const runRestore = async (dryRun) => {
    if (dryRun) { setPreviewing(true); setPreview(null); } else { setRestoring(true); setRestoreResult(null); }
    setRestoreError(null);
    try {
      const res = await apiService.backups.restore(restorePayload(dryRun));
      const result = res.data.restore || null;
      if (dryRun) {
        setPreview(result);
      } else {
        setRestoreResult(result);
        setNotice('Restore complete');
        await load();
      }
    } catch (e) {
      setRestoreError(serverMessage(e, dryRun ? 'Preview failed' : 'Restore failed'));
    } finally {
      if (dryRun) setPreviewing(false); else setRestoring(false);
    }
  };

  const archiveReady = source === 'stored' ? !!storedName : !!archive;
  const confirmMatches = !!organizationName
    && confirmText.trim().toLowerCase() === organizationName.toLowerCase();
  const canSubmitRestore = archiveReady && selected.length > 0 && confirmMatches
    && !writeDisabled && !restoring && !previewing;

  const sortedExports = useMemo(
    () => [...exports].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)),
    [exports]
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>What a backup contains</AlertTitle>
        An export is a single JSON archive of your organization&apos;s data: detections, cases, indicators,
        entities, integration and agent configurations, enrichment providers, automation rules, notifications
        and ID counters. User accounts and audit logs are included for reference but are{' '}
        <strong>never overwritten by a restore</strong>. Excubia additionally keeps platform-level Firestore
        backups with point-in-time recovery, so these exports are for your own copies and for restoring
        selected collections yourself.
      </Alert>

      {storageError && (
        <Alert severity="warning" sx={{ mb: 3 }}>{storageError}</Alert>
      )}

      {writeDisabled && (
        <Alert severity="info" sx={{ mb: 3 }}>
          You can view backups, but exporting, deleting and restoring require the company administrator role.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Automatic weekly exports */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<ScheduleIcon />}
              title="Automatic weekly exports"
              subheader="Runs Sunday 02:00 UTC"
            />
            <CardContent>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!settings.weeklyExport}
                    onChange={(e) => setSettings((prev) => ({ ...prev, weeklyExport: e.target.checked }))}
                    disabled={writeDisabled}
                  />
                }
                label="Store a weekly export automatically"
              />
              <TextField
                fullWidth
                sx={{ mt: 2 }}
                label="Keep the last N automatic exports"
                type="number"
                value={settings.keepCount}
                onChange={(e) => setSettings((prev) => ({ ...prev, keepCount: e.target.value }))}
                inputProps={{ min: 1, max: 52 }}
                helperText="Between 1 and 52. Older automatic exports are deleted; manual copies are kept."
                disabled={writeDisabled || !settings.weeklyExport}
              />
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Tooltip title={writeTip}>
                  <span>
                    <Button
                      variant="contained"
                      startIcon={savingSettings ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                      onClick={handleSaveSettings}
                      disabled={writeDisabled || savingSettings}
                    >
                      Save
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Export now */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardHeader
              avatar={<BackupIcon />}
              title="Export now"
              subheader="Download an archive, or keep a copy in Excubia's archive storage"
            />
            <CardContent>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Tooltip title={writeTip}>
                  <span>
                    <Button
                      variant="contained"
                      startIcon={downloading ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
                      onClick={handleDirectDownload}
                      disabled={writeDisabled || downloading}
                    >
                      Download export
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title={writeTip}>
                  <span>
                    <Button
                      variant="outlined"
                      startIcon={storing ? <CircularProgress size={18} /> : <ArchiveIcon />}
                      onClick={handleStoreCopy}
                      disabled={writeDisabled || storing}
                    >
                      Store a copy in Excubia
                    </Button>
                  </span>
                </Tooltip>
              </Box>
              {lastExport && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {lastExport.name} · {formatSize(lastExport.size)} · {formatAbsolute(lastExport.exportedAt)}
                  </Typography>
                  <CountsTable counts={lastExport.counts} />
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Stored exports */}
        <Grid item xs={12}>
          <Card>
            <CardHeader
              avatar={<ArchiveIcon />}
              title="Stored exports"
              subheader="Archives kept in Excubia for this organization"
              action={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Tooltip title={writeTip || 'Restore from a JSON archive on your computer'}>
                    <span>
                      <Button
                        size="small"
                        startIcon={<UploadIcon />}
                        onClick={openUploadRestore}
                        disabled={writeDisabled}
                      >
                        Restore from file
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Refresh">
                    <span>
                      <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
                    </span>
                  </Tooltip>
                </Box>
              }
            />
            <CardContent>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Size</TableCell>
                      <TableCell>Origin</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedExports.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <BackupIcon color="disabled" sx={{ fontSize: 36 }} />
                          <Typography color="text.secondary">
                            No stored exports yet. Store a copy above, or enable automatic weekly exports.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedExports.map((row) => (
                        <TableRow key={row.name} hover>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{row.name}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Tooltip title={formatAbsolute(row.createdAt)}>
                              <span>{formatRelativeTime(row.createdAt)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatSize(row.size)}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              variant="outlined"
                              color={row.auto ? 'default' : 'primary'}
                              label={row.auto ? 'Automatic' : 'Manual'}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <Tooltip title={writeTip || 'Download'}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDownloadStored(row.name)}
                                  disabled={writeDisabled || rowBusy === row.name}
                                >
                                  {rowBusy === row.name
                                    ? <CircularProgress size={18} />
                                    : <DownloadIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={writeTip || 'Restore from this export'}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => openStoredRestore(row)}
                                  disabled={writeDisabled}
                                >
                                  <RestoreIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title={writeTip || 'Delete'}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => setDeleteTarget(row)}
                                  disabled={writeDisabled}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onClose={deleting ? undefined : () => setDeleteTarget(null)}>
        <DialogTitle>Delete this export?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{deleteTarget?.name}</strong> ({formatSize(deleteTarget?.size)}) will be removed from
            Excubia&apos;s archive storage. This cannot be undone, and any copy you downloaded is unaffected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore dialog */}
      <Dialog open={restoreOpen} onClose={closeRestore} fullWidth maxWidth="sm">
        <DialogTitle>Restore from {source === 'stored' ? 'a stored export' : 'a file'}</DialogTitle>
        <DialogContent dividers>
          {restoreError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRestoreError(null)}>{restoreError}</Alert>}

          {source === 'stored' ? (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Archive: <strong style={{ fontFamily: 'monospace' }}>{storedName}</strong>
            </Typography>
          ) : (
            <Box sx={{ mb: 2 }}>
              <Button component="label" variant="outlined" startIcon={<UploadIcon />} disabled={restoring}>
                Choose a .json archive
                <input type="file" accept=".json,application/json" hidden onChange={handleFile} />
              </Button>
              {uploadName && (
                <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace' }}>{uploadName}</Typography>
              )}
              {archiveError && <Alert severity="error" sx={{ mt: 1 }}>{archiveError}</Alert>}
              {archive && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Valid Excubia export from <strong>{formatAbsolute(archive.exportedAt)}</strong>
                    {archive.company?.name ? ` · ${archive.company.name}` : ''}
                  </Typography>
                  <CountsTable counts={archive.counts} />
                </Box>
              )}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Mode</FormLabel>
            <RadioGroup value={mode} onChange={(e) => { setMode(e.target.value); setPreview(null); }}>
              <FormControlLabel
                value="merge"
                control={<Radio />}
                label="Merge (recommended) – writes the archived records over the current ones and keeps everything else"
              />
              <FormControlLabel
                value="replace"
                control={<Radio />}
                label="Replace – empties each selected collection first, then writes the archived records"
              />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset" sx={{ mb: 2, display: 'block' }}>
            <FormLabel component="legend">Collections to restore</FormLabel>
            <FormGroup>
              {restorable.map((name) => {
                const count = archive?.counts?.[name];
                return (
                  <FormControlLabel
                    key={name}
                    control={
                      <Checkbox
                        checked={selected.includes(name)}
                        onChange={() => toggleCollection(name)}
                        size="small"
                      />
                    }
                    label={
                      <span>
                        {labelFor(name)}
                        {count !== undefined && (
                          <Typography component="span" variant="caption" color="text.secondary">
                            {' '}({Number(count).toLocaleString()} in archive)
                          </Typography>
                        )}
                      </span>
                    }
                  />
                );
              })}
            </FormGroup>
            {selected.length === 0 && <FormHelperText error>Select at least one collection.</FormHelperText>}
          </FormControl>

          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              onClick={() => runRestore(true)}
              disabled={!archiveReady || selected.length === 0 || previewing || restoring || writeDisabled}
              startIcon={previewing ? <CircularProgress size={16} /> : <RestoreIcon />}
            >
              Preview (dry run)
            </Button>
            {preview && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  A {preview.mode} restore of the archive from {formatAbsolute(preview.exportedAt)} would write:
                </Typography>
                <CountsTable counts={preview.collections} />
              </Box>
            )}
          </Box>

          {restoreResult ? (
            <Alert severity="success">
              <AlertTitle>Restore complete</AlertTitle>
              <Typography variant="body2" gutterBottom>
                {restoreResult.mode} restore of the archive from {formatAbsolute(restoreResult.exportedAt)}:
              </Typography>
              <CountsTable counts={restoreResult.collections} />
            </Alert>
          ) : (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                A restore overwrites current records with the archived ones and cannot be undone. User accounts
                and audit logs are never touched.
              </Alert>
              <TextField
                fullWidth
                label="Type your organization name to confirm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                helperText={organizationName
                  ? `Type "${organizationName}" exactly.`
                  : 'Your organization name could not be determined; the server will validate what you type.'}
                error={!!confirmText && !confirmMatches}
                disabled={restoring}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRestoreOpen(false)} disabled={previewing || restoring}>
            {restoreResult ? 'Close' : 'Cancel'}
          </Button>
          {!restoreResult && (
            <Tooltip title={writeTip}>
              <span>
                <Button
                  color="error"
                  variant="contained"
                  onClick={() => runRestore(false)}
                  disabled={!canSubmitRestore}
                  startIcon={restoring ? <CircularProgress size={16} color="inherit" /> : <RestoreIcon />}
                >
                  Restore
                </Button>
              </span>
            </Tooltip>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!notice}
        autoHideDuration={4000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setNotice(null)} variant="filled">{notice}</Alert>
      </Snackbar>
    </Box>
  );
};

export default BackupSettings;
